import React, { useState, useEffect } from 'react';
import WelcomePage from './components/WelcomePage/WelcomePage';
import MainApp from './components/MainApp/MainApp';
import AdminConsole from './components/AdminConsole/AdminConsole';
import './App.css';
import SimulatorPage from './components/MainApp/Chat/DialogueManagement/simulator/SimulatorPage';

type UserRole = 'admin' | 'user';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('user');

  useEffect(() => {
    // Check if user is already logged in from localStorage
    const loginStatus = localStorage.getItem('isLoggedIn');
    const user = localStorage.getItem('currentUser');
    let savedRole = (localStorage.getItem('userRole') as UserRole) || 'user';
    
    // Also check userData in case role is stored there
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (userData.role) {
          savedRole = userData.role.toLowerCase() === 'admin' ? 'admin' : 'user';
          console.log('Found role in userData:', userData.role, '-> savedRole:', savedRole);
        }
      } catch (e) {
        console.error('Error parsing userData:', e);
      }
    }
    
    // Debug: Log what we're reading from localStorage
    console.log('App useEffect - loginStatus:', loginStatus, 'user:', user, 'savedRole:', savedRole);

    if (loginStatus === 'true' && user) {
      setIsLoggedIn(true);
      setCurrentUser(user);
      setUserRole(savedRole);
      // Update localStorage to ensure consistency
      localStorage.setItem('userRole', savedRole);
      console.log('App - Set userRole to:', savedRole);
      console.log('========================================');
      if (savedRole === 'admin') {
        console.log('✅ ADMIN DETECTED FROM LOCALSTORAGE - Will show AdminConsole');
      } else {
        console.log('👤 REGULAR USER FROM LOCALSTORAGE - Will show MainApp');
      }
      console.log('========================================');
    }
  }, []);

  const handleLogin = (userId: string, isAdmin = false) => {
    setIsLoggedIn(true);
    setCurrentUser(userId);
    const role: UserRole = isAdmin ? 'admin' : 'user';
    setUserRole(role);
    localStorage.setItem('userRole', role);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser('');
    setUserRole('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userRole');
  };

  // Development-only route gate for Dialogue Management Simulator
  if (typeof window !== 'undefined' && window.location && window.location.pathname === '/dialogueManagement') {
    return <SimulatorPage />;
  }

  if (!isLoggedIn) {
    return <WelcomePage onLogin={handleLogin} />;
  }

  // Derive effective role (dev convenience: treat known admin usernames as admin)
  const isAdmin =
    userRole === 'admin' ||
    currentUser.toLowerCase() === 'admin' ||
    currentUser.toLowerCase() === 'admin_dev';

  // Debug: Log the current state
  console.log('App render - isLoggedIn:', isLoggedIn, 'userRole:', userRole, 'currentUser:', currentUser, 'isAdmin:', isAdmin);
  console.log('========================================');
  if (isAdmin) {
    console.log('✅ ADMIN DETECTED - Rendering AdminConsole');
    console.log('========================================');
    return <AdminConsole currentUser={currentUser} onLogout={handleLogout} />;
  }

  console.log('👤 REGULAR USER - Rendering MainApp (regular chat)');
  console.log('========================================');
  return <MainApp currentUser={currentUser} onLogout={handleLogout} />;
}

export default App;
